import { RotateCcw } from 'lucide-react';
import { layerSchema, selectActiveLayer, useStudio } from '../../store/studioStore';
import { TEXT_TECHNIQUE_ID } from '../../store/layer';
import { getTechnique } from '../../techniques/registry';
import { ParamList, ParamSearch } from '../params/ParamList';

/**
 * Right-hand panel: the parameters of whichever layer is selected.
 *
 * Nothing here knows about any specific technique — it reads the schema from the
 * registry and renders it. A technique added in the future appears here complete with
 * grouping, search, locks and exploration ranges, without this file changing.
 */
export function ParamsPanel(): JSX.Element {
  const layer = useStudio(selectActiveLayer);
  const search = useStudio((s) => s.paramSearch);
  const setParamSearch = useStudio((s) => s.setParamSearch);
  const setLayerValue = useStudio((s) => s.setLayerValue);
  const setLayerBulk = useStudio((s) => s.setLayerBulk);
  const toggleLayerLock = useStudio((s) => s.toggleLayerLock);
  const randomizeLayerParam = useStudio((s) => s.randomizeLayerParam);
  const resetLayerParams = useStudio((s) => s.resetLayerParams);

  const schema = layerSchema(layer);
  const technique = layer.techniqueId === TEXT_TECHNIQUE_ID ? null : getTechnique(layer.techniqueId);

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 border-b border-ink-800 px-3 py-2.5">
        <div className="mb-2 flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12px] font-medium text-ink-100">{layer.name}</div>
            <div className="truncate text-[10px] text-ink-500">
              {technique ? technique.description : 'Der gesetzte Text als Ebene im Stapel'}
            </div>
          </div>
          <button
            type="button"
            className="ghost-button h-6 w-6 p-0"
            title="Alle Parameter dieses Layers zurücksetzen"
            onClick={() => resetLayerParams(layer.id)}
          >
            <RotateCcw size={11} />
          </button>
        </div>
        <ParamSearch value={search} onChange={setParamSearch} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3">
        {schema.length === 0 ? (
          <p className="py-6 text-center text-[11px] leading-relaxed text-ink-500">
            Diese Technik hat keine Parameter.
          </p>
        ) : (
          <ParamList
            schema={schema}
            values={layer.values}
            bulk={layer.bulk}
            onChange={(key, value) => setLayerValue(layer.id, key, value)}
            onBulkChange={(key, spec) => setLayerBulk(layer.id, key, spec)}
            onToggleLock={(key) => toggleLayerLock(layer.id, key)}
            onRandomize={(key) => randomizeLayerParam(layer.id, key)}
            search={search}
          />
        )}
      </div>
    </div>
  );
}
